import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  CreditCard,
  Check,
  X,
  Zap,
  Star,
  Rocket,
  Save,
  Loader2
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { formatError } from '../../utils/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSubscriptions() {
  const { getAuthHeader } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [plans, setPlans] = useState({});
  const [boostPricing, setBoostPricing] = useState({ property_boosts: [], agency_boosts: [] });
  const [loading, setLoading] = useState(true);
  const [savingBoostPricing, setSavingBoostPricing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [agenciesRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/agencies`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/subscription/plans`)
      ]);
      setAgencies(agenciesRes.data.agencies);
      setPlans(plansRes.data.plans);
      if (plansRes.data.boost_pricing) {
        setBoostPricing(plansRes.data.boost_pricing);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSubscription = async (agencyId, plan) => {
    try {
      await axios.put(`${API_URL}/api/admin/agencies/${agencyId}/subscription?plan=${plan}`, {}, {
        headers: getAuthHeader()
      });
      setAgencies(prev => prev.map(a =>
        a.id === agencyId ? { ...a, subscription: plan } : a
      ));
      toast.success(`Subscription updated to ${plan}`);
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || 'Failed to update subscription';
      toast.error(message);
    }
  };

  const updateBoostTierPrice = (type, index, field, value) => {
    setBoostPricing(prev => {
      const updated = { ...prev };
      const tiers = [...(updated[type] || [])];
      tiers[index] = { ...tiers[index], [field]: field === 'days' || field === 'price' ? Number(value) : value };
      updated[type] = tiers;
      return updated;
    });
  };

  const saveBoostPricing = async () => {
    setSavingBoostPricing(true);
    try {
      await axios.put(`${API_URL}/api/admin/boost-pricing`, boostPricing, {
        headers: getAuthHeader()
      });
      toast.success('Boost pricing updated!');
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || error.message;
      toast.error('Failed to update boost pricing: ' + message);
    } finally {
      setSavingBoostPricing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="grid grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-subscriptions">
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Subscription & Boost Management</h1>
        <p className="text-navy-600">Manage plans, featured limits, and boost pricing</p>
      </div>

      {/* Plans Overview */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {Object.entries(plans).map(([key, plan]) => (
          <Card key={key} className={key === 'premium' ? 'border-purple-500 border-2' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                {key === 'premium' && <Badge className="bg-purple-500">Popular</Badge>}
              </div>
              <p className="text-3xl font-bold text-navy-900">
                {plan.price === 0 ? 'Free' : `₹${plan.price}`}
                {plan.price > 0 && <span className="text-sm font-normal text-navy-500">/month</span>}
              </p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500" />
                  {plan.max_properties === -1 ? 'Unlimited' : plan.max_properties} properties
                </li>
                <li className="flex items-center gap-2 text-sm">
                  {plan.notifications ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                  Enquiry notifications
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  {plan.premium_boosts} premium boosts/month
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500" />
                  {plan.analytics === 'full' ? 'Full analytics' : 'Basic analytics'}
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Star className="w-4 h-4 text-amber-500" />
                  {plan.featured_properties_limit || 'N/A'} featured {(plan.featured_properties_limit || 0) === 1 ? 'property' : 'properties'}
                </li>
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Boost Pricing Configuration */}
      <Card className="mb-8" data-testid="boost-pricing-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Rocket className="w-5 h-5 text-amber-500" />
            Boost Pricing Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Property Boosts */}
          <div>
            <h3 className="font-semibold text-navy-900 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              Single Property Boost Tiers
            </h3>
            <div className="grid gap-3">
              {(boostPricing.property_boosts || []).map((tier, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Days</Label>
                      <Input
                        type="number"
                        value={tier.days}
                        onChange={(e) => updateBoostTierPrice('property_boosts', i, 'days', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Price (₹)</Label>
                      <Input
                        type="number"
                        value={tier.price}
                        onChange={(e) => updateBoostTierPrice('property_boosts', i, 'price', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={tier.label}
                        onChange={(e) => updateBoostTierPrice('property_boosts', i, 'label', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-navy-500 whitespace-nowrap">₹{Math.round(tier.price / tier.days)}/day</p>
                </div>
              ))}
            </div>
          </div>

          {/* Agency Boosts */}
          <div>
            <h3 className="font-semibold text-navy-900 mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-purple-500" />
              Agency-Wide Boost Tiers
            </h3>
            <div className="grid gap-3">
              {(boostPricing.agency_boosts || []).map((tier, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Days</Label>
                      <Input
                        type="number"
                        value={tier.days}
                        onChange={(e) => updateBoostTierPrice('agency_boosts', i, 'days', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Price (₹)</Label>
                      <Input
                        type="number"
                        value={tier.price}
                        onChange={(e) => updateBoostTierPrice('agency_boosts', i, 'price', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={tier.label}
                        onChange={(e) => updateBoostTierPrice('agency_boosts', i, 'label', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-navy-500 whitespace-nowrap">₹{Math.round(tier.price / tier.days)}/day</p>
                </div>
              ))}
            </div>
          </div>

          <Button
            onClick={saveBoostPricing}
            disabled={savingBoostPricing}
            className="w-full bg-navy-900 hover:bg-navy-800 gap-2"
            data-testid="save-boost-pricing-btn"
          >
            {savingBoostPricing ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4" /> Save Boost Pricing</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Agency Subscriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-sky-500" />
            Agency Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {agencies.map((agency) => (
              <div
                key={agency.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-navy-50 rounded-lg gap-4"
                data-testid={`subscription-row-${agency.id}`}
              >
                <div>
                  <p className="font-medium text-navy-900">{agency.company_name}</p>
                  <p className="text-sm text-navy-500">{agency.email}</p>
                  {agency.subscription_expires && (
                    <p className="text-xs text-navy-400 mt-1">
                      Expires: {new Date(agency.subscription_expires).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={
                    agency.subscription === 'premium' ? 'bg-purple-500' :
                      agency.subscription === 'basic' ? 'bg-blue-500' : 'bg-gray-500'
                  }>
                    {agency.subscription}
                  </Badge>
                  <Select
                    value={agency.subscription}
                    onValueChange={(v) => updateSubscription(agency.id, v)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
